import type { AccountRole } from "./account-role";
const PREFIX = "tinkr.location:";
const SAFE_PART = /^[a-z][a-z0-9_-]{0,49}$/;
type LocationHint = { section: string; subtab?: string; at: number };
export function clearDashboardLocations(): void {
  try {
    for (const storage of [localStorage, sessionStorage]) {
      for (const key of Object.keys(storage)) if (key.startsWith(PREFIX)) storage.removeItem(key);
    }
  } catch { /* storage is optional */ }
}
export function restoreDashboardLocation(userId: string, role: AccountRole, sections: readonly string[]): LocationHint | null {
  try {
    const raw = localStorage.getItem(PREFIX + userId + ":" + role);
    if (!raw) return null;
    const hint = JSON.parse(raw) as LocationHint;
    if (!sections.includes(hint.section) || !Number.isFinite(hint.at) || hint.at > Date.now() + 60_000 || Date.now() - hint.at > 30 * 86400_000) return null;
    return { section: hint.section, at: hint.at, ...(role === "admin" && hint.subtab && SAFE_PART.test(hint.subtab) ? { subtab: hint.subtab } : {}) };
  } catch { return null; }
}
export function rememberDashboardLocation(userId: string, role: AccountRole, section: string): void {
  if (!SAFE_PART.test(section)) return;
  const subtab = new URLSearchParams(location.search).get("subtab");
  const hint: LocationHint = { section, at: Date.now(), ...(role === "admin" && subtab && SAFE_PART.test(subtab) ? { subtab } : {}) };
  try { localStorage.setItem(PREFIX + userId + ":" + role, JSON.stringify(hint)); } catch { /* optional */ }
}
/** Same-origin SPA navigation: keeps caches and lets both route owners restore. */
export function navigateDashboardUrl(target: string): boolean {
  let url: URL;
  try { url = new URL(target, location.origin); } catch { return false; }
  if (url.origin !== location.origin || url.pathname !== "/") return false;
  url.searchParams.set("screen", "dashboard");
  history.pushState(history.state, "", url.pathname + url.search + url.hash);
  window.dispatchEvent(new PopStateEvent("popstate"));
  return true;
}
export function readPanelView<T extends string>(userId: string, panel: string, choices: readonly T[], fallback: T): T {
  try {
    const value = sessionStorage.getItem(PREFIX + userId + ":panel:" + panel);
    return choices.includes(value as T) ? value as T : fallback;
  } catch { return fallback; }
}
export function rememberPanelView(userId: string, panel: string, value: string): void {
  try { sessionStorage.setItem(PREFIX + userId + ":panel:" + panel, value); } catch { /* optional */ }
}
